<script setup>
import { ref, computed, defineAsyncComponent, nextTick, watch, onMounted } from 'vue'

const props = defineProps({
  additionalLanguages: {
    type: Array,
    default: () => []
  },
  siteHandle: {
    type: [String],
    default: ''
  }
})

// Dynamically load the language list component
const LocaleSelectorList = defineAsyncComponent(() => 
  import('./list.vue')
)

const filteredLanguages = computed(() => {
  if (!Array.isArray(props.additionalLanguages)) {
    return []
  }

  return props.additionalLanguages.filter(
    (language) => language?.siteHandle !== props.siteHandle
  )
})

const hasLanguages = computed(() => filteredLanguages.value.length > 0)

const currentLanguageLabel = computed(() => {
  if (props.siteHandle === 'delightAi_ko_KR') {
    return 'KO'
  }

  if (props.siteHandle === 'delightAi_ja_JP') {
    return 'JA'
  }

  return 'EN'
})

// State
const isOpen = ref(false)
const rootRef = ref(null)
const localeListRef = ref(null)
const buttonRef = ref(null)

// Methods
const openMenu = () => {
  if (!hasLanguages.value) return
  isOpen.value = true
}

const closeMenu = () => {
  isOpen.value = false
}

const toggleMenu = () => {
  if (!hasLanguages.value) return
  isOpen.value = !isOpen.value
}

const focusFirstLink = async () => {
  await nextTick()
  if (localeListRef.value && localeListRef.value.focusFirstLink) {
    localeListRef.value.focusFirstLink()
  }
}

const returnFocusToButton = () => {
  closeMenu()
  if (buttonRef.value) {
    buttonRef.value.focus()
  }
}

const handleKeydown = async (event) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault()
    const wasOpen = isOpen.value
    toggleMenu()
    if (!wasOpen) {
      await nextTick()
      focusFirstLink()
    }
  } else if (event.key === 'ArrowDown') {
    if (isOpen.value) {
      event.preventDefault()
      focusFirstLink()
    } else {
      event.preventDefault()
      toggleMenu()
      await nextTick()
      focusFirstLink()
    }
  }
}

// Handle focus leaving the component
const handleFocusOut = (event) => {
  // Check if the new focus target is outside the component
  if (rootRef.value && !rootRef.value.contains(event.relatedTarget)) {
    closeMenu()
  }
}

// Watch for menu opening to focus first link
watch(isOpen, async (newValue) => {
  if (newValue) {
    await nextTick()
    focusFirstLink()
  }
})

// Lifecycle
onMounted(() => {
  if (rootRef.value) {
    rootRef.value.addEventListener('focusout', handleFocusOut)
  }
})

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
  <div v-if="hasLanguages"
    ref="rootRef"
    class="locale-selector --desk-selector"
    :class="{ 'is-open': isOpen }"
    @mouseenter="openMenu"
    @mouseleave="closeMenu">
    
    <button ref="buttonRef"
      type="button"
      class="toggle"
      :aria-expanded="isOpen.toString()"
      aria-haspopup="listbox"
      aria-controls="locale-selector-list"
      aria-label="Select language"
      @keydown="handleKeydown"
    >
      <svg class="toggle__icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M11.9902 2.09961C17.465 2.09974 21.9004 6.53537 21.9004 12C21.9004 17.4646 17.465 21.9003 11.9902 21.9004C6.52559 21.9004 2.09961 17.4649 2.09961 12C2.09961 6.5351 6.52559 2.09961 11.9902 2.09961ZM9.99414 16.0283C10.4272 17.4686 11.0822 18.8083 11.918 20.0166L12 20.1357L12.082 20.0166C12.9178 18.8083 13.5728 17.4686 14.0059 16.0283L14.0449 15.9004H9.95508L9.99414 16.0283ZM4.99316 16.0498C5.96517 17.7306 7.51493 19.0164 9.37793 19.6543L9.61914 19.7373L9.49805 19.5127C8.90226 18.4105 8.44506 17.2181 8.12695 15.9756L8.10742 15.9004H4.90625L4.99316 16.0498ZM15.873 15.9756C15.5549 17.2181 15.0977 18.4105 14.502 19.5127L14.3809 19.7373L14.6221 19.6543C16.4853 19.0163 18.035 17.7202 19.0068 16.0498L19.0938 15.9004H15.8926L15.873 15.9756ZM4.16309 9.97559C4.0015 10.6219 3.90039 11.3003 3.90039 12C3.90039 12.6997 4.0015 13.3781 4.16309 14.0244L4.18164 14.0996H7.75293L7.73926 13.9883C7.65948 13.3301 7.59961 12.6743 7.59961 12C7.59961 11.3257 7.65948 10.6699 7.73926 10.0117L7.75293 9.90039H4.18164L4.16309 9.97559ZM9.56055 9.98633C9.47028 10.6383 9.40039 11.3134 9.40039 12C9.40039 12.6867 9.47032 13.352 9.56055 14.0137L9.57227 14.0996H14.4277L14.4395 14.0137C14.5297 13.352 14.5996 12.6867 14.5996 12C14.5996 11.3134 14.5297 10.6383 14.4395 9.98633L14.4268 9.90039H9.57324L9.56055 9.98633ZM16.2607 10.0117C16.3405 10.6699 16.4004 11.3257 16.4004 12C16.4004 12.6743 16.3405 13.3301 16.2607 13.9883L16.2471 14.0996H19.8184L19.8369 14.0244C19.9985 13.3781 20.0996 12.6997 20.0996 12C20.0996 11.3003 19.9985 10.6219 19.8369 9.97559L19.8184 9.90039H16.2471L16.2607 10.0117ZM9.37793 4.3457C7.51493 4.98358 5.96517 6.26944 4.99316 7.9502L4.90625 8.09961H8.10742L8.12695 8.02441C8.44506 6.78191 8.90226 5.58951 9.49805 4.4873L9.61914 4.2627L9.37793 4.3457ZM11.918 3.9834C11.0822 5.19174 10.4272 6.53143 9.99414 7.97168L9.95508 8.09961H14.0449L14.0059 7.97168C13.5728 6.53143 12.9178 5.19174 12.082 3.9834L12 3.86426L11.918 3.9834ZM14.502 4.4873C15.0977 5.58951 15.5549 6.78191 15.873 8.02441L15.8926 8.09961H19.0938L19.0068 7.9502C18.035 6.27983 16.4853 4.98366 14.6221 4.3457L14.3809 4.2627L14.502 4.4873Z" fill="black" stroke="white" stroke-width="0.2"/>
      </svg>
      <span class="toggle__label">
        {{ currentLanguageLabel }}
      </span>
      <span class="toggle__caret" aria-hidden="true">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <g clip-path="url(#clip0_7320_2043)">
            <path d="M11.06 5.72656L8 8.7799L4.94 5.72656L4 6.66656L8 10.6666L12 6.66656L11.06 5.72656Z" fill="black"/>
          </g>
          <defs>
            <clipPath id="clip0_7320_2043">
              <rect width="16" height="16" fill="white"/>
            </clipPath>
          </defs>
        </svg>
      </span>
    </button>

    <transition name="fade">
      <LocaleSelectorList 
        ref="localeListRef"
        v-if="isOpen"
        :languages="filteredLanguages"
        :on-first-link-shift-tab="returnFocusToButton"
      />
    </transition>
  </div>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

.locale-selector {
  position: relative;
  display: flex;
  align-items: center;
  height: 72px;
}

.locale-selector .toggle {
  display: inline-flex;
  align-items: center;
  padding: 6px 0;
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
}

.locale-selector .toggle__icon {
  flex-shrink: 0;
}

.locale-selector .toggle__label {
  font-weight: 500;
  line-height: 22px;
  text-transform: uppercase;
  color: var(--color-black);
  margin-inline: 4px;
}

.locale-selector .toggle__caret {
  display: inline-flex;
  align-items: center;
  transition: transform 0.2s ease;
}

.locale-selector.is-open .toggle__caret {
  transform: rotate(180deg);
}

</style>
